import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Webhook, Zap, MessageSquare, Calendar as CalIcon, Database, Check, ExternalLink, RefreshCw, Shield, Power, Eye, EyeOff } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";

const PROVIDERS = [
  { key: "attio", name: "Attio", desc: "Two-way sync of applicants, members, events, and registrations to Attio CRM.", icon: Database, accent: "purple" },
  { key: "slack", name: "Slack", desc: "Post to a channel when someone registers, checks in, or requests a slot.", icon: MessageSquare, accent: "green" },
  { key: "gcal", name: "Google Calendar", desc: "Auto-create calendar events for attendees and investors.", icon: CalIcon, accent: "peach" },
  { key: "zapier", name: "Zapier", desc: "Trigger Zaps on any lifecycle event through our REST API.", icon: Zap, accent: "purple" },
];

const AVAILABLE_EVENTS = [
  "event.registered", "event.checked_in", "event.feedback_submitted",
  "session.slot_requested", "session.slot_booked", "session.slot_cancelled",
  "community.broadcast",
];

// A full Luma pass runs in the BACKGROUND (POST /admin/luma/sync returns
// {started:true} immediately) and only writes its luma_sync_runs row when it
// finishes — a real calendar takes 15+ minutes. Until now the page fetched the
// runs once on mount, so the result appeared only if you happened to reload
// afterwards, and the sole feedback was a toast that scrolled away. These two
// keep the in-flight pass visible.
const RUNS_POLL_MS = 15_000;   // while a pass is believed to be running
const RUNS_IDLE_MS = 60_000;   // otherwise — catches the hourly auto-sync too
// Survives a reload: without it, refreshing mid-pass drops back to "no idea".
const SYNC_MARK_KEY = "vd:luma:syncStartedAt";
// Stop claiming "running" long after any plausible pass, so a crashed worker
// cannot leave a spinner up forever.
const SYNC_MAX_MS = 45 * 60_000;

export default function AdminIntegrations() {
  const [endpoints, setEndpoints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ url: "", events: [] });
  const [busy, setBusy] = useState(false);
  const [luma, setLuma] = useState(null);
  const [lumaBusy, setLumaBusy] = useState(false);
  const [lumaRuns, setLumaRuns] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);
  syncingRef.current = syncing;

  const load = () => api.get("/webhooks").then((r) => setEndpoints(r.data)).catch(() => setEndpoints([]));

  const loadRuns = useCallback(async () => {
    try {
      const r = await api.get("/admin/luma/runs");
      const rows = r.data || [];
      setLumaRuns(rows.slice(0, 3));
      return rows;
    } catch { return null; }
  }, []);

  const loadLuma = useCallback(() => {
    api.get("/admin/luma/config").then((r) => setLuma(r.data)).catch(() => {});
    loadRuns();
  }, [loadRuns]);

  useEffect(() => { load(); loadLuma(); }, [loadLuma]);

  // Reloading the page mid-pass used to lose all knowledge that one was running.
  const readMark = () => {
    try { return JSON.parse(localStorage.getItem(SYNC_MARK_KEY) || "null"); } catch { return null; }
  };
  useEffect(() => {
    const mark = readMark();
    if (!mark) return;
    if (Date.now() - mark.at < SYNC_MAX_MS) setSyncing(true);
    else localStorage.removeItem(SYNC_MARK_KEY);
  }, []);

  // Poll the run log: quickly while a pass is in flight, lazily otherwise so a
  // finished auto-sync also lands without a reload. A pass is done the moment a
  // run newer than the one present at trigger time appears — the row is only
  // written when the pass completes.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const rows = await loadRuns();
      if (cancelled || !rows || !syncingRef.current) return;
      const mark = readMark();
      if (!mark) { setSyncing(false); return; }
      const newest = rows[0]?.started_at || "";
      if (newest && newest !== mark.baseline) {
        localStorage.removeItem(SYNC_MARK_KEY);
        setSyncing(false);
        const r = rows[0];
        toast.success(`Sync finished — ${r.events} events · ${r.guests} guests${r.errors?.length ? ` · ${r.errors.length} failed` : ""}`);
      } else if (Date.now() - mark.at >= SYNC_MAX_MS) {
        localStorage.removeItem(SYNC_MARK_KEY);
        setSyncing(false);
        toast.error("Sync did not report back — check the server logs");
      }
    };
    const id = setInterval(tick, syncing ? RUNS_POLL_MS : RUNS_IDLE_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [syncing, loadRuns]);

  const toggleLuma = async () => {
    if (!luma) return;
    if (!luma.is_leader && !luma.auto_sync_enabled) {
      if (!window.confirm("This environment is a follower (SYNC_LEADER=false), so auto-sync won't fire from here even if enabled. The toggle is stored in the shared DB, so enabling it here will activate the loop on the leader (Production) deployment. Continue?")) return;
    }
    setLumaBusy(true);
    try {
      const r = await api.patch("/admin/luma/config", { auto_sync_enabled: !luma.auto_sync_enabled });
      setLuma(r.data);
      toast.success(r.data.auto_sync_enabled ? "Auto-sync enabled" : "Auto-sync disabled");
    } catch { toast.error("Failed to update"); }
    finally { setLumaBusy(false); }
  };

  // Bulk public-grid visibility for imported Luma events. Private events are
  // never affected: their visibility ('internal') keeps them off the public
  // grid regardless of this flag.
  const bulkGrid = async (on) => {
    const msg = on
      ? "Put every PUBLIC Luma event back on the public events page?\n\nPrivate Luma events and internal templates stay hidden — their visibility keeps them off the grid regardless."
      : "Hide EVERY imported Luma event from the public events page?\n\nNative events are unaffected. Re-show them with “Show all public”, or one at a time from Admin → Events.";
    if (!window.confirm(msg)) return;
    setLumaBusy(true);
    try {
      const r = await api.post("/admin/luma/grid/bulk", { on });
      toast.success(`${on ? "Shown on" : "Hidden from"} the public grid${r.data.updated != null ? ` — ${r.data.updated} events` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLumaBusy(false); }
  };

  const runManualSync = async () => {
    if (!window.confirm("Run a Luma sync now? This will fetch fresh events + guests from Luma and update the shared DB. Safe to run — no emails will be sent.")) return;
    setLumaBusy(true);
    try {
      // Remember which run was newest BEFORE triggering: the pass is finished
      // when something newer shows up, since the row is written on completion.
      const baseline = lumaRuns[0]?.started_at || "";
      const r = await api.post("/admin/luma/sync");
      // The full pass runs in the background now (awaiting it in-request hit
      // the proxy timeout and silently truncated the sync on big calendars).
      if (r.data.started) {
        try { localStorage.setItem(SYNC_MARK_KEY, JSON.stringify({ at: Date.now(), baseline })); } catch { /* private mode */ }
        setSyncing(true);
      } else {
        toast.success(`${r.data.events} events · ${r.data.guests} guests synced`);
      }
      loadLuma();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setLumaBusy(false); }
  };

  const toggleEvent = (ev) => setForm((f) => ({ ...f, events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev] }));

  const create = async () => {
    if (!form.url || !form.events.length) return toast.error("URL and at least one event required");
    setBusy(true);
    try {
      await api.post("/webhooks", form);
      toast.success("Endpoint added");
      setForm({ url: "", events: [] });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this endpoint?")) return;
    try { await api.delete(`/webhooks/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <AdminShell
      testId="admin-integrations-page"
      eyebrow="Settings"
      title="Integrations"
      subtitle="Connect Venture Dock to the tools you already use. Native integrations are on the roadmap — in the meantime, wire anything via webhooks."
    >
          {/* Luma sync (active) */}
          {luma && (
            <div className="glass-panel p-6 mt-8" data-testid="luma-integration">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: "hsl(var(--vd-purple) / 0.24)", color: "hsl(var(--vd-purple-ink))" }}>
                    <RefreshCw size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading text-lg leading-tight">Luma</div>
                      <span className="chip chip-green text-[10px]"><Check size={9} />Connected</span>
                      <span className={`chip text-[10px] ${luma.is_leader ? "chip-purple" : "chip-cream-2"}`} title={luma.is_leader ? "This deploy runs the sync loop" : "This deploy follows — auto-sync is inert here"}>
                        <Shield size={9} />{luma.environment_label}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 max-w-lg">
                      Pull Luma events + guests into your dashboard hourly. Runs only when both the DB toggle and the SYNC_LEADER env flag are on — so Preview can never accidentally double-sync against Production.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Disabled while a pass is in flight: a second full pass on
                      top of a running one is pure duplicate load. */}
                  <button onClick={runManualSync} disabled={lumaBusy || syncing} className="btn-ghost text-sm" data-testid="luma-manual-sync">
                    <RefreshCw size={12} className={syncing ? "animate-spin" : undefined} />
                    {syncing ? "Syncing…" : lumaBusy ? "Working…" : "Sync now"}
                  </button>
                  {/* Was a checkbox labelled "Auto-sync off", which is genuinely
                      ambiguous: an unchecked box beside the word "off" could mean
                      the current state OR what ticking it does. A button labelled
                      with the ACTION cannot be misread, and the state is already
                      stated twice below (DB toggle + Effective state). */}
                  {/* Both directions. `public_on_grid` defaults to true on
                      INSERT, which only covers newly imported events — an
                      existing row keeps whatever it has, by design, so a
                      re-sync never undoes a deliberate choice. That also means
                      a bulk hide is permanent for those rows until a bulk show
                      puts them back. Private events are unaffected either way:
                      their visibility keeps them off the public grid. */}
                  <button
                    onClick={() => bulkGrid(true)}
                    disabled={lumaBusy}
                    className="btn-ghost text-sm"
                    data-testid="luma-show-all"
                    title="Put every public Luma event back on the public events page"
                  >
                    <Eye size={12} /> Show all public
                  </button>
                  <button
                    onClick={() => bulkGrid(false)}
                    disabled={lumaBusy}
                    className="btn-ghost text-sm"
                    data-testid="luma-hide-all"
                    title="Remove all imported Luma events from the public events page"
                  >
                    <EyeOff size={12} /> Hide all
                  </button>
                  <button
                    onClick={toggleLuma}
                    disabled={lumaBusy}
                    className="btn-ghost text-sm"
                    data-testid="luma-auto-toggle"
                    title={luma.auto_sync_enabled
                      ? "Stop the hourly Luma pull"
                      : "Pull Luma events + guests every hour"}
                  >
                    <Power size={12} />
                    {luma.auto_sync_enabled ? "Turn auto-sync off" : "Turn auto-sync on"}
                  </button>
                </div>
              </div>

              {/* Status grid */}
              <div className="mt-5 grid md:grid-cols-3 gap-3 text-xs">
                <StatusRow label="DB toggle" value={luma.auto_sync_enabled ? "Enabled" : "Disabled"} ok={luma.auto_sync_enabled} />
                <StatusRow label="Leader (env)" value={luma.is_leader ? "SYNC_LEADER=true" : "SYNC_LEADER=false"} ok={luma.is_leader} mono />
                <StatusRow label="Effective state" value={(luma.auto_sync_enabled && luma.is_leader) ? "Running hourly" : "Paused"} ok={luma.auto_sync_enabled && luma.is_leader} />
              </div>

              {/* In-flight banner. A full pass walks every event AND paginates
                  each roster — 15+ minutes on a real calendar — so without this
                  the page looked idle and people re-triggered or assumed a
                  partial count was the final one. */}
              {syncing && (
                <div className="mt-5 p-3 rounded-lg border flex items-center gap-2 text-xs"
                     style={{ borderColor: "hsl(var(--vd-green) / 0.4)", background: "hsl(var(--vd-green) / 0.08)" }}
                     data-testid="luma-sync-inflight">
                  <RefreshCw size={13} className="animate-spin" style={{ color: "hsl(var(--vd-green))" }} />
                  <span>
                    <strong>Sync running.</strong> It walks every event and its full guest list —
                    typically 15+ minutes on a large calendar. Counts below update when it finishes;
                    you can leave this page.
                  </span>
                </div>
              )}

              {/* Recent runs */}
              {lumaRuns.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Recent syncs</div>
                  <div className="mt-2 space-y-1.5">
                    {lumaRuns.map((r, i) => (
                      <div key={i} className="text-xs font-mono flex items-center justify-between p-2 rounded-lg border border-white/60 bg-white/40">
                        <span className="text-muted-foreground">{new Date(r.started_at).toLocaleString()}</span>
                        {/* `listed` is what Luma returned, `events` what we kept.
                            Showing only the latter made a truncated pass look
                            like a small calendar. */}
                        <span>
                          {r.events} events{r.listed != null && r.listed !== r.events ? ` of ${r.listed}` : ""}
                          {r.pages ? ` · ${r.pages}p` : ""} · {r.guests} guests
                          {r.skipped ? <span className="text-destructive"> · {r.skipped} skipped</span> : ""}
                          {r.errors?.length ? <span className="text-destructive"> · {r.errors.length} err</span> : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Native integrations */}
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {PROVIDERS.map((p) => {
              const bg = { green: "hsl(var(--vd-green) / 0.14)", purple: "hsl(var(--vd-purple) / 0.24)", peach: "hsl(var(--vd-peach) / 0.14)" }[p.accent];
              const color = { green: "hsl(var(--vd-green))", purple: "hsl(var(--vd-purple-ink))", peach: "hsl(var(--vd-peach-ink))" }[p.accent];
              const Icon = p.icon;
              return (
                <div key={p.key} className="glass-panel p-5" data-testid={`integration-${p.key}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: bg, color }}>
                        <Icon size={18} />
                      </div>
                      <div>
                        <div className="font-heading text-lg leading-tight">{p.name}</div>
                        <div className="chip chip-cream text-[10px] mt-1">Coming soon</div>
                      </div>
                    </div>
                    <button disabled className="btn-ghost text-sm opacity-60 cursor-not-allowed" data-testid={`integration-connect-${p.key}`}>Connect</button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Webhooks */}
          <div className="glass-panel p-6 mt-8" data-testid="webhooks-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl flex items-center justify-center" style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}>
                  <Webhook size={16} />
                </div>
                <div>
                  <div className="font-heading text-lg">Webhooks</div>
                  <div className="text-xs text-muted-foreground">Get notified on any lifecycle event. Retries with exponential backoff.</div>
                </div>
              </div>
              <button onClick={() => setShowForm(true)} className="btn-primary text-sm" data-testid="add-webhook-btn"><Plus size={12} />Add endpoint</button>
            </div>

            {showForm && (
              <div className="mt-5 pt-4 border-t border-white/40" data-testid="webhook-form">
                <Overline>New endpoint</Overline>
                <input className="input-field mt-2" placeholder="https://your-app.com/webhooks/venturedock" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="webhook-url" />
                <div className="mt-3">
                  <div className="overline mb-2">Events to send</div>
                  <div className="flex flex-wrap gap-1.5">
                    {AVAILABLE_EVENTS.map((ev) => {
                      const on = form.events.includes(ev);
                      return (
                        <button key={ev} onClick={() => toggleEvent(ev)} className={`chip text-[11px] font-mono ${on ? "chip-green" : "chip-cream-2"}`} data-testid={`event-${ev}`}>
                          {on && <Check size={10} />} {ev}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-4 flex gap-2 justify-end">
                  <button onClick={() => { setShowForm(false); setForm({ url: "", events: [] }); }} className="btn-ghost text-sm">Cancel</button>
                  <button onClick={create} disabled={busy} className="btn-primary text-sm" data-testid="create-webhook">{busy ? "Saving…" : "Save endpoint"}</button>
                </div>
              </div>
            )}

            <div className="mt-5 space-y-2">
              {endpoints.length === 0 && !showForm && (
                <div className="p-6 text-center text-sm text-muted-foreground">No endpoints yet.</div>
              )}
              {endpoints.map((ep) => (
                <div key={ep.id} className="p-4 rounded-2xl border border-white/60 bg-white/40 flex items-start justify-between gap-3" data-testid={`endpoint-${ep.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <ExternalLink size={12} className="text-muted-foreground shrink-0" />
                      <span className="font-mono text-xs truncate">{ep.url}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(ep.events || []).map((ev) => <span key={ev} className="chip chip-cream text-[10px] font-mono">{ev}</span>)}
                    </div>
                    <div className="mt-2 text-[10px] font-mono text-muted-foreground">Created {new Date(ep.created_at).toLocaleDateString()}</div>
                  </div>
                  <button onClick={() => remove(ep.id)} className="btn-ghost text-xs" data-testid={`delete-endpoint-${ep.id}`}>Delete</button>
                </div>
              ))}
            </div>
          </div>

          {/* API */}
          <div className="glass-panel p-6 mt-6" data-testid="api-section">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-lg">REST API</div>
                <div className="text-xs text-muted-foreground mt-1">Programmatic access to events, registrations, and community. Bearer token auth.</div>
              </div>
              <span className="chip chip-green text-[10px]">v1</span>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-2 text-xs font-mono">
              <div className="p-3 rounded-xl bg-white/50 border border-white/60"><span className="chip chip-green text-[10px] mr-2">GET</span>/api/events</div>
              <div className="p-3 rounded-xl bg-white/50 border border-white/60"><span className="chip chip-green text-[10px] mr-2">GET</span>/api/community/attendees</div>
              <div className="p-3 rounded-xl bg-white/50 border border-white/60"><span className="chip chip-purple text-[10px] mr-2">POST</span>/api/events/{"{id}"}/register</div>
              <div className="p-3 rounded-xl bg-white/50 border border-white/60"><span className="chip chip-purple text-[10px] mr-2">POST</span>/api/community/broadcast</div>
            </div>
          </div>
    </AdminShell>
  );
}

function StatusRow({ label, value, ok, mono }) {
  return (
    <div className="p-3 rounded-xl border border-white/60 bg-white/40 flex items-center justify-between gap-2">
      <span className="text-muted-foreground uppercase tracking-widest text-[10px]">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-xs`} style={{ color: ok ? "hsl(var(--vd-green))" : "hsl(var(--muted-foreground))" }}>{value}</span>
    </div>
  );
}
